export const BUSINESS_AMERICAS_START_DATE = "2027-02-15";
export const PREMIUM_ECONOMIC_CABIN = "PREMIUM_ECONOMIC";
export const COMFORT_CABIN = "COMFORT";
export const BUSINESS_AMERICAS_CABIN = "BUSINESS_AMERICAS";
export const GOL_PREMIUM_CABIN = "GOL_PREMIUM";
export const API_DATA_FLAG = "api-data";
export const NOVA_LOPA_WEB_FLAG = "ENABLE_NOVA_LOPA_SMLPRJI740";
const CORE_LD_CLIENT_ID = "5ff64893991a4c0aa4974fc7";

const CABINS_RENAMED_ON_VIGENCIA = [PREMIUM_ECONOMIC_CABIN, COMFORT_CABIN, BUSINESS_AMERICAS_CABIN];

type ApiDataFlag = {
  constants?: {
    WEB?: Record<string, unknown>;
  };
  value?: {
    constants?: {
      WEB?: Record<string, unknown>;
    };
  };
};

type CoreHelpers = {
  getConstants?: (key: string) => unknown;
};

const CORE_CONTEXT_KEY = "__CoreContext";

const isMissingCoreConstant = (value: unknown): boolean => {
  if (!value || typeof value !== "object") {
    return false;
  }

  return Object.prototype.hasOwnProperty.call(value, "message");
};

const readNovaLopaFromCore = (): boolean | null => {
  const browserWindow = window as unknown as Record<string, { Helpers?: CoreHelpers } | undefined>;
  const helpers = browserWindow[CORE_CONTEXT_KEY]?.Helpers;
  if (!helpers || typeof helpers.getConstants !== "function") {
    return null;
  }

  try {
    const value = helpers.getConstants(NOVA_LOPA_WEB_FLAG);
    if (value === true || value === false) {
      return value;
    }
    if (isMissingCoreConstant(value)) {
      return false;
    }
    return null;
  } catch {
    return null;
  }
};

const readNovaLopaFromFlags = (flags?: Record<string, unknown> | null): boolean => {
  const apiData = flags?.[API_DATA_FLAG] as ApiDataFlag | undefined;
  const webConstants = apiData?.constants?.WEB ?? apiData?.value?.constants?.WEB;
  return webConstants?.[NOVA_LOPA_WEB_FLAG] === true;
};

let relayFlag: boolean | null = null;

const envTokenFromHost = (hostname: string): string => {
  if (hostname === "localhost") {
    return "hml5";
  }

  const smilesDomain = hostname.indexOf("smiles.com.br") > -1;
  const hostPattern = smilesDomain ? /^(.*)\.smiles\.com\.br/ : /^(.*)\.voebiz\.com\.br/;
  const match = hostPattern.exec(hostname);
  if (!match || !match[1] || match[1].length <= 3) {
    return "";
  }

  return match[1]
    .replace(/portal|voebiz/g, "")
    .replace(/uat/g, "hml")
    .replace(/^-/, "");
};

const readNovaLopaFromRelayPayload = (payload: unknown): boolean => {
  const apiData = (payload as Record<string, ApiDataFlag> | null)?.[API_DATA_FLAG];
  return apiData?.value?.constants?.WEB?.[NOVA_LOPA_WEB_FLAG] === true;
};

export const resetGolPremiumFlagCache = (): void => {
  relayFlag = null;
};

export const loadGolPremiumFlag = async (hostname = window.location.hostname): Promise<boolean> => {
  const fromCore = readNovaLopaFromCore();
  if (fromCore !== null) {
    return fromCore;
  }

  const token = envTokenFromHost(hostname);
  if (!token) {
    relayFlag = false;
    return false;
  }

  try {
    const userKey = window.btoa(`{"key":"${token.toUpperCase()}"}`);
    const response = await window.fetch(
      `https://ldrelay-${token}.smiles.com.br/sdk/evalx/${CORE_LD_CLIENT_ID}/users/${userKey}`,
    );
    relayFlag = readNovaLopaFromRelayPayload(await response.json());
  } catch {
    relayFlag = false;
  }

  return relayFlag;
};

export const isGolPremiumEnabled = (flags?: Record<string, unknown> | null): boolean => {
  const fromCore = readNovaLopaFromCore();
  if (fromCore !== null) {
    return fromCore;
  }
  if (relayFlag !== null) {
    return relayFlag;
  }
  return readNovaLopaFromFlags(flags);
};

const ISO_DATE_PREFIX = /^(\d{4}-\d{2}-\d{2})/;

const CABIN_LABELS: Record<string, string> = {
  ECONOMIC: "Econômica",
  PREMIUM_ECONOMIC: "Premium Economy",
  BUSINESS_AMERICAS: "Premium Economy",
  GOL_PREMIUM: "GOL Premium",
  BUSINESS: "Executiva",
  FIRST_CLASS: "Primeira Classe",
  COMFORT: "Premium",
};

type FlightDateValue = Date | string | null | undefined;

const parseDate = (value: Date | string): Date | null => {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatLocalDate = (date: Date): string => {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const formatZonedDate = (date: Date, timeZone: string): string => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const year = parts.find((part) => part.type === "year")?.value ?? "";
  const month = parts.find((part) => part.type === "month")?.value ?? "";
  const day = parts.find((part) => part.type === "day")?.value ?? "";

  return `${year}-${month}-${day}`;
};

const toCalendarDate = (value: FlightDateValue, timeZone?: string): string | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "string") {
    const isoDate = ISO_DATE_PREFIX.exec(value);
    if (isoDate) {
      return isoDate[1];
    }
  }

  const date = parseDate(value);
  if (!date) {
    return null;
  }

  return timeZone ? formatZonedDate(date, timeZone) : formatLocalDate(date);
};

const toDateOrder = (calendarDate: string): number => {
  const parts = calendarDate.split("-");
  const year = parts[0] || "0";
  const month = parts[1] || "0";
  const day = parts[2] || "0";
  return Number(year) * 10000 + Number(month) * 100 + Number(day);
};

export const isBusinessAmericasVigencia = (flightDate?: FlightDateValue, timeZone?: string): boolean => {
  const calendarDate = toCalendarDate(flightDate, timeZone);
  if (calendarDate === null) {
    return false;
  }

  return toDateOrder(calendarDate) >= toDateOrder(BUSINESS_AMERICAS_START_DATE);
};

export const getCabinDisplayName = (
  cabin?: string | null,
  flightDate?: FlightDateValue,
  timeZone?: string,
  golPremiumEnabled = false,
): string => {
  if (!cabin) {
    return "";
  }

  if (
    golPremiumEnabled &&
    CABINS_RENAMED_ON_VIGENCIA.indexOf(cabin) !== -1 &&
    isBusinessAmericasVigencia(flightDate, timeZone)
  ) {
    return CABIN_LABELS[GOL_PREMIUM_CABIN];
  }

  return CABIN_LABELS[cabin] || cabin;
};

type CabinDeparture = {
  date?: FlightDateValue;
  airport?: { timeZone?: string } | null;
} | null;

export const getCabinDisplayNameFromDeparture = (
  cabin?: string | null,
  departure?: CabinDeparture,
  golPremiumEnabled = false,
): string => {
  return getCabinDisplayName(cabin, departure?.date, departure?.airport?.timeZone, golPremiumEnabled);
};
